/**
 * ████████╗ ██████╗ ██╗     ██╗███╗   ██╗ ██████╗
 * ╚══██╔══╝██╔═══██╗██║     ██║████╗  ██║██╔═══██╗
 *    ██║   ██║   ██║██║     ██║██╔██╗ ██║██║   ██║
 *    ██║   ██║   ██║██║     ██║██║╚██╗██║██║   ██║
 *    ██║   ╚██████╔╝███████╗██║██║ ╚████║╚██████╔╝
 *    ╚═╝    ╚═════╝ ╚══════╝╚═╝╚═╝  ╚═══╝ ╚═════╝
 *
 * (c) Copyright 2021-present Rakuten Kobo Inc. (https://www.kobo.com)
 *
 * Global variable definitions that can be used throughout all tolino
 * applications. These are tolino specific, but not app specific. For
 * application specific global variables see the GLOBALS.js file in each
 * application's source code folder.
 */

// -----------------------------------------------------------------------------
// IMPORTS
// -----------------------------------------------------------------------------

// Internal dependencies
import MONOREPO_GLOBALS from './GLOBALS';


// -----------------------------------------------------------------------------
// GLOBAL DEFINITIONS
// -----------------------------------------------------------------------------

const BACKEND_ENVIRONMENTS = Object.freeze({
  PROD: 'prod',
  PRODREF: 'prodref',
  TEST01: 'test01'
});

const GLOBALS = Object.freeze(Object.assign({ }, MONOREPO_GLOBALS, {
  APP_IDENTIFIERS_CLIENT_TYPES: {
    "affiliate": "TOLINO_WEBREADER",
    "android": "TOLINO_ANDROID",
    "authapp": "TOLINO_WEBREADER",
    "ios": "TOLINO_IOS",
    "management": "TOLINO_WEBREADER",
    "reseller-selection": "TOLINO_WEBREADER",
    "samplereader": "TOLINO_WEBREADER",
    "sonicchair": "TOLINO_APP_SONICCHAIR",
    "webreader": "TOLINO_WEBREADER"
  },

  APP_IDENTIFIER_RESELLER_GROUPS: {
    "affiliate": 27,
    "android": 26,
    "authapp": 27,
    "ios": 25,
    "management": 27,
    "reseller-selection": 1000,
    "samplereader": 27,
    "sonicchair": 27,
    "webreader": 27
  },

  APPSTATE: {
    ENGINES: {
      INDEXEDDB: {
        DEFAULT_OBJECT_STORE_NAME: 'tolino',
        VERSION: 1
      },
      SQLITE: {
        DB_NAME: 'tolino.sqlite',
        DB_LOCATION: 'default',
        DB_VERSION: "1.0",
        TABLE_NAME: 'store'
      }
    },
    KEYPREFIX: 'tolino:',
    STORAGES: {
      APP: {
        NAME: 'app',
        VERSION: 1
      },
      DOWNLOAD: {
        NAME: 'downloadinfo',
        VERSION: 1
      },
      INVENTORY: {
        NAME: 'inventory',
        VERSION: 1
      }
    }
  },

  CONSTANTS:{
    O_AUTH: {
      KEY_AUTH_APP_TOKEN_DATA: "tolino_authTokenData"
    },
    PUSH_TO_CLOUD: {
      KEY_AUTH_APP_DATA :"tolino_authProcessData",
      KEY_P2C_ID: "tolino_p2c_pubid"
    },
    REMOTE_CONFIG: {
      KEY_AUTH_APP_REMOTE_CONFIG: "tolino_authRemoteConfig",
      KEY_OAUTH_SIM_ENABLED: "oauthsim"
    }
  },

  ENVIRONMENT: {
    ENVIRONMENTS: BACKEND_ENVIRONMENTS
  },

  ERROR: {
    1000: {
      errorCode: -1000,
      errorMessage: 'We are sorry, but something went terribly wrong and the application needs to be restarted. We apologize for the inconvenience!'
    },
    1001: {
      errorCode: -1001,
      errorMessage: "We are sorry, but something does not look right. Press OK to restart the application."
    }
  },

  HARDWARE: {
    DEMO_HARDWARE_ID: "DEMO:HARDWARE:ID"
  },

  MEDIA_QUERY: {
    BREAKPOINT_IDS: {
      XSMALL: 'extraSmallScreen',
      SMALLPHONE: 'smallPhoneScreen',
      SMALL: 'smallScreen',
      MEDIUM: 'mediumScreen',
      LARGE: 'largeScreen',
      XLARGE: 'extraLargeScreen'
    },
    BREAKPOINTS: {
      XSMALL: 0,
      SMALLPHONE:375,
      SMALL: 480,
      MEDIUM: 768,
      LARGE: 1024,
      XLARGE: 1440
    },
    ORIENTATION: {
      LANDSCAPE: 'landscape',
      PORTRAIT: 'portrait'
    }
  },


  MESSAGE_BUS: {
    CHANNEL: {
      APP: 'app',
      SERVICE: 'service',
      READER: 'reader'
    },

    MESSAGE: {
      APP_DEVELOP_LOGIN: 'app/iframeLogin',
      APP_READY: 'app/ready',
      APP_RESET: 'app/reset',
      APP_WINDOW_RESIZE: 'app/windowResize',
      DEVICE_BREAKPOINT_CHANGE: 'device/breakpointChange',
      DEVICE_DIMENSIONS_CHANGE: 'device/dimensionsChange',
      DEVICE_ORIENTATION_CHANGE: 'device/orientationChange',
      END_OF_BOOK: 'endOfBook',
      NETWORK_STATE_CHANGE: 'network/stateChange',
      RX_POPUP_VISIBILITY: 'rx/popup/visibility',
      SERVICE_BACKEND_HTTP_ERROR: 'service/backend/httpError',
      SERVICE_BACKEND_NETWORK_ERROR: 'service/backend/networkError',
      SERVICE_DOWNLOAD_PROGRESS:'service/download/progress',
      SERVICE_HARDWARE_DEVICE_LIMIT: 'service/hardware/deviceLimit',
      SERVICE_OAUTH_NO_TOKEN:'service/oauth/noToken',
      SERVICE_REMOTECONFIG_NOCONFIG: 'service/remoteConfig/noConfig',
      SERVICE_TRACKPLAYER_PROGRESS: 'service/trackplayer/progress',
      SERVICE_TRACKPLAYER_STATE: 'service/trackplayer/state'
    }
  },

  NETWORK: {
    BOSH_REST_ENDPOINT: 'bosh/rest/',
    TIMEOUT: 10000  // in milliseconds
  },

  OAUTH: {
    SIMULATOR: {
      OAUTH_CLIENT_ID: 'simulatorId',
      OAUTH_SCOPE: 'simulatorScope',
      OAUTH_TOKEN_DEMO_USER: 'RGFzIGlzdCBlaW4gRGVtby1Ub2tlbg==',
      RESELLER_ID: 1,
      URL_OAUTH_AUTHORIZATION: 'https://simulator.prodref.pageplace.de/oauth-simulator/',
      URL_OAUTH_REDIRECT_WEB: 'https://127.0.0.1:3000',
      URL_OAUTH_REDIRECT_NATIVE: 'epublishing://login',
      URL_OAUTH_REVOKETOKEN: 'https://simulator.prodref.pageplace.de/oauth-simulator/oauth/revoke',
      URL_OAUTH_ACCESSTOKEN: 'https://simulator.prodref.pageplace.de/oauth-simulator/oauth/token'
    }
  },

  URLS: {
    // TODO: This should not be hardcoded here, but instead be coming from
    //       some configuration file
    APP_AUTH: 'https://webreader.mytolino.com/auth/index.html'
  }
}));


// -----------------------------------------------------------------------------
// EXPORTS
// -----------------------------------------------------------------------------

export default GLOBALS;
